/**
 * Navigation subsystem for the player robot — path planning, spline
 * following, stuck detection, movement, and evasive strafing.
 *
 * Extracted from `Robot.ts` as a companion class. `RobotNavigator` owns
 * the navigation-specific state (spline, cooldown, stuck timers, wander
 * phase, strafe direction) and exposes high-level operations that take
 * the `Robot` reference in each method so the navigator can read and
 * update chassis kinematics (x, y, angle, currentSpeed) without having
 * to surface that state through its own API.
 *
 * Immutable references the navigator needs (labyrinth, exploration,
 * base coords, expedition angle, physics) are cached at construction
 * time so per-method parameter lists stay short.
 *
 * Nothing here is pure — every top-level method mutates either the
 * navigator's own state or the robot's chassis. The file-local helpers
 * `smoothPath` / `pickExplorationTarget` are the only pure bits.
 */

import type { Labyrinth } from './Labyrinth';
import type { ExplorationData } from './ExplorationData';
import type { Robot } from './Robot';
import { findPath } from './Pathfinder';
import { Spline } from './Spline';
import { wrapAngle } from './mathUtils';
import {
  ALL_DIRS,
  EVASION_STRAFE_SPEED_FRAC,
  EVASION_DIR_CHANGE_MIN, EVASION_DIR_CHANGE_MAX,
} from './RobotConstants';
import {
  canMoveTo, hasLineOfWalk, hasRevealedLineOfWalk,
} from './RobotVision';

/** Construction-time arguments for `RobotNavigator`. */
export interface NavigatorInit {
  labyrinth: Labyrinth;
  exploration: ExplorationData;
  /** Shared mutable set — `Robot.revealVision` writes, navigator reads. */
  visitedNumeric: Set<number>;
  baseX: number;
  baseY: number;
  expeditionAngle: number;
  combatApproachSpeed: number;
  turnSpeed: number;
  /** Initial robot world-space position (used to seed stuck-detection anchor). */
  startX: number;
  startY: number;
}

export class RobotNavigator {
  // ── Immutable refs / physics (set in constructor) ──
  private readonly labyrinth: Labyrinth;
  private readonly exploration: ExplorationData;
  private readonly visitedNumeric: Set<number>;
  private readonly baseX: number;
  private readonly baseY: number;
  private readonly expeditionAngle: number;
  private readonly combatApproachSpeed: number;
  private readonly turnSpeed: number;

  // ── Spline path state ──
  spline: Spline | null = null;
  splineDist = 0;
  /** Cooldown gate between successive pathfinder calls (seconds). */
  pathCooldown = 0;

  // ── Stuck detection ──
  private stuckTimer = 0;
  private lastX: number;
  private lastY: number;
  private lastAngle = 0;
  private stuckCheckTimer = 0;
  private consecutiveStucks = 0;

  // ── Natural wander (drives spline steering jitter) ──
  private wanderTime = 0;
  private wanderPhase = Math.random() * Math.PI * 2;

  // ── Evasive strafe state ──
  private strafeDir = 1;       // +1 cw, -1 ccw
  private strafeDirTimer = 0;  // seconds until next dir flip

  constructor(init: NavigatorInit) {
    this.labyrinth = init.labyrinth;
    this.exploration = init.exploration;
    this.visitedNumeric = init.visitedNumeric;
    this.baseX = init.baseX;
    this.baseY = init.baseY;
    this.expeditionAngle = init.expeditionAngle;
    this.combatApproachSpeed = init.combatApproachSpeed;
    this.turnSpeed = init.turnSpeed;
    this.lastX = init.startX;
    this.lastY = init.startY;
  }

  // =======================================================
  // Wander tick — called once per frame from Robot.update
  // =======================================================
  tickWander(dt: number): void {
    this.wanderTime += dt;
  }

  // =======================================================
  // Spline-path queries
  // =======================================================

  needsNewPath(): boolean {
    if (!this.spline) return true;
    if (this.splineDist >= this.spline.totalLength) return true;
    return false;
  }

  clearPath(): void {
    this.spline = null;
    this.splineDist = 0;
  }

  /**
   * Sample the current spline as a polyline from `(curX, curY)` to the
   * end of the spline. Used by the overlay renderer to draw the route.
   * Returns `[]` when there is no active path.
   */
  getPathPoints(curX: number, curY: number, stepDist = 0.5): [number, number][] {
    if (!this.spline || this.splineDist >= this.spline.totalLength) return [];
    const points: [number, number][] = [[curX, curY]];
    for (let d = this.splineDist; d < this.spline.totalLength; d += stepDist) {
      points.push(this.spline.positionAt(d));
    }
    points.push(this.spline.positionAt(this.spline.totalLength));
    return points;
  }

  // =======================================================
  // Primitive movement — tryMove / moveToward
  // =======================================================

  /**
   * Attempt to move the robot by `(mx, my)` world-space units with wall
   * sliding: if the diagonal step is blocked, try the two axis-aligned
   * components independently so the chassis grinds along walls instead
   * of sticking. Returns true iff any motion happened.
   */
  tryMove(robot: Robot, mx: number, my: number): boolean {
    const nx = robot.x + mx;
    const ny = robot.y + my;
    const lab = this.labyrinth;
    if (canMoveTo(lab, nx, ny)) {
      robot.x = nx; robot.y = ny;
      return true;
    } else if (canMoveTo(lab, nx, robot.y)) {
      robot.x = nx;
      return true;
    } else if (canMoveTo(lab, robot.x, ny)) {
      robot.y = ny;
      return true;
    }
    return false;
  }

  /**
   * Turn toward `(tx, ty)` and move forward, with speed attenuated by
   * the cosine of the turn error so the chassis doesn't slide sideways
   * when mid-pivot. Updates `robot.angle`, `robot.x/y`, `robot.currentSpeed`.
   */
  moveToward(robot: Robot, dt: number, tx: number, ty: number, speed: number): void {
    const dx = tx - robot.x;
    const dy = ty - robot.y;
    const desiredAngle = Math.atan2(dy, dx);
    const angleDiff = wrapAngle(desiredAngle - robot.angle);
    const maxTurn = this.turnSpeed * dt;
    robot.angle += Math.sign(angleDiff) * Math.min(Math.abs(angleDiff), maxTurn);
    robot.angle = wrapAngle(robot.angle);

    const curSpeed = speed * Math.max(0.2, Math.cos(angleDiff));
    const mx = Math.cos(robot.angle) * curSpeed * dt;
    const my = Math.sin(robot.angle) * curSpeed * dt;
    robot.currentSpeed = this.tryMove(robot, mx, my) ? curSpeed : 0;
  }

  // =======================================================
  // High-level approach — pathfind if needed, else straight-line
  // =======================================================

  /**
   * Move toward a world-space target. If line-of-walk is clear, drive
   * straight; otherwise plan a spline path and follow it. Throttles
   * pathfinder calls via `pathCooldown`.
   */
  approachEntity(robot: Robot, dt: number, tx: number, ty: number, speed: number): void {
    if (hasLineOfWalk(this.labyrinth, robot.x, robot.y, tx, ty)) {
      this.moveToward(robot, dt, tx, ty, speed);
      this.clearPath();
      return;
    }

    this.pathCooldown -= dt;
    if (this.needsNewPath() && this.pathCooldown <= 0) {
      this.planPathTo(robot, Math.floor(tx), Math.floor(ty));
      this.pathCooldown = 0.5;
    }

    this.followSpline(robot, dt, speed);
  }

  /**
   * Orbit around an enemy while maintaining combat range. Computes a
   * target point offset perpendicular to the enemy with a radial pull
   * correction, then delegates to `moveToward` so the chassis must
   * physically turn before moving. Periodically flips strafe direction,
   * and reverses immediately when the robot is wall-stalled.
   */
  performEvasiveStrafe(
    robot: Robot, dt: number,
    ex: number, ey: number, dist: number, combatRange: number,
  ): void {
    this.strafeDirTimer -= dt;
    if (this.strafeDirTimer <= 0) {
      this.strafeDir = Math.random() < 0.5 ? 1 : -1;
      this.strafeDirTimer = EVASION_DIR_CHANGE_MIN
        + Math.random() * (EVASION_DIR_CHANGE_MAX - EVASION_DIR_CHANGE_MIN);
    }

    const toEnemyAngle = Math.atan2(ey - robot.y, ex - robot.x);

    const idealDist = combatRange * 0.8;
    const radialPull = (dist - idealDist) * 0.4;
    const strafeAngle = toEnemyAngle + this.strafeDir * Math.PI * 0.5;
    const strafeSpeed = this.combatApproachSpeed * EVASION_STRAFE_SPEED_FRAC;

    const dirX = Math.cos(strafeAngle) * strafeSpeed + Math.cos(toEnemyAngle) * radialPull;
    const dirY = Math.sin(strafeAngle) * strafeSpeed + Math.sin(toEnemyAngle) * radialPull;
    const mag = Math.hypot(dirX, dirY);
    const reach = 2.0;
    const targetX = robot.x + (dirX / mag) * reach;
    const targetY = robot.y + (dirY / mag) * reach;

    const prevX = robot.x;
    const prevY = robot.y;

    this.moveToward(robot, dt, targetX, targetY, strafeSpeed);

    // Only flag as wall-stalled when the chassis is already aiming at the
    // strafe target yet failed to translate. While the robot is still
    // pivoting toward the new direction, zero translation is expected and
    // must not trigger a strafe flip — otherwise every direction change
    // would spin-loop against itself.
    const moved = Math.hypot(robot.x - prevX, robot.y - prevY);
    const aimAngle = Math.atan2(targetY - robot.y, targetX - robot.x);
    const angleErr = Math.abs(wrapAngle(aimAngle - robot.angle));
    if (moved < strafeSpeed * dt * 0.05 && angleErr < 0.6) {
      this.strafeDir = -this.strafeDir;
      this.strafeDirTimer = EVASION_DIR_CHANGE_MIN
        + Math.random() * (EVASION_DIR_CHANGE_MAX - EVASION_DIR_CHANGE_MIN);
    }
  }

  // =======================================================
  // Stuck detection — advance the spline or bail the path
  // when the chassis hasn't meaningfully moved.
  // =======================================================

  updateStuckDetection(robot: Robot, dt: number): void {
    this.stuckCheckTimer += dt;
    if (this.stuckCheckTimer < 0.3) return;

    const moved = Math.hypot(robot.x - this.lastX, robot.y - this.lastY);
    // A pivoting-in-place chassis translates little but rotates a lot —
    // count that as progress so the stuck detector doesn't shred the
    // spline while the robot is mid-turn.
    const turned = Math.abs(wrapAngle(robot.angle - this.lastAngle));
    if (moved < 0.05 && turned < 0.5) {
      this.stuckTimer += this.stuckCheckTimer;
    } else {
      this.stuckTimer = 0;
      this.consecutiveStucks = 0;
    }
    this.lastX = robot.x;
    this.lastY = robot.y;
    this.lastAngle = robot.angle;
    this.stuckCheckTimer = 0;

    if (this.stuckTimer > 0.6) {
      this.consecutiveStucks++;
      if (this.spline) {
        // Jump ahead on the spline (further each consecutive stall)
        const jumpDist = (1 + this.consecutiveStucks) * 2;
        this.splineDist = Math.min(this.splineDist + jumpDist, this.spline.totalLength);
        if (this.splineDist >= this.spline.totalLength) {
          this.clearPath();
        }
      } else {
        this.clearPath();
      }
      this.stuckTimer = 0;
      this.pathCooldown = 0;
    }
  }

  // =======================================================
  // Spline following — smooth Bezier curve traversal
  // =======================================================

  followSpline(robot: Robot, dt: number, speed: number): void {
    if (!this.spline || this.splineDist >= this.spline.totalLength) return;

    const [sx, sy] = this.spline.positionAt(this.splineDist);
    const [tanX, tanY] = this.spline.tangentAt(this.splineDist);

    // Position correction: pull back onto the curve when drifted off.
    // Damped (gain 1.2, cap 0.35) so a transient drift during a turn
    // doesn't swing desiredAngle hard enough to set up oscillation.
    const corrDx = sx - robot.x;
    const corrDy = sy - robot.y;
    const drift = Math.hypot(corrDx, corrDy);
    const blend = Math.min(drift * 1.2, 0.35);
    const corrNx = drift > 0.01 ? corrDx / drift : 0;
    const corrNy = drift > 0.01 ? corrDy / drift : 0;
    const dirX = tanX * (1 - blend) + corrNx * blend;
    const dirY = tanY * (1 - blend) + corrNy * blend;

    const wander = Math.sin(this.wanderTime * 0.7 + this.wanderPhase) * 0.06;
    const desiredAngle = Math.atan2(dirY, dirX) + wander;

    const angleDiff = wrapAngle(desiredAngle - robot.angle);
    const maxTurnSpline = this.turnSpeed * dt;
    robot.angle += Math.sign(angleDiff) * Math.min(Math.abs(angleDiff), maxTurnSpline);
    robot.angle = wrapAngle(robot.angle);

    const facingDot = Math.cos(angleDiff);
    const curSpeed = speed * Math.max(0.15, facingDot);
    const mx = Math.cos(robot.angle) * curSpeed * dt;
    const my = Math.sin(robot.angle) * curSpeed * dt;

    if (this.tryMove(robot, mx, my)) {
      this.splineDist += curSpeed * dt;
      robot.currentSpeed = curSpeed;
    } else {
      // Bumped a wall — nudge splineDist forward to recover.
      this.splineDist += speed * dt * 0.5;
      robot.currentSpeed = 0;
    }

    if (this.splineDist >= this.spline.totalLength) {
      this.clearPath();
    }
  }

  // =======================================================
  // Path planning
  // =======================================================

  /**
   * Pick a frontier target (expedition-biased) and plan a path to it;
   * fall back to a random walk if no scorable target exists; final
   * fallback is to head home so energy drain ends the run cleanly
   * rather than freezing on a fully-explored map.
   */
  planExplorationPath(robot: Robot): void {
    const target = this.pickExplorationTarget(robot.tileX, robot.tileY, robot.angle);
    if (!target) {
      this.planRandomWalk(robot);
      if (!this.spline) {
        this.planPathTo(robot, this.baseX, this.baseY);
      }
      return;
    }

    this.planPathTo(robot, target[0], target[1]);

    if (!this.spline) {
      this.exploration.unreachable.add(`${target[0]},${target[1]}`);
      this.exploration.frontier.delete(`${target[0]},${target[1]}`);
      this.planRandomWalk(robot);
    }
  }

  /**
   * Walk to a random nearby walkable tile to get unstuck or discover
   * new frontier. First 8 attempts bias toward the expedition angle
   * (±90°); the final 4 sample the full 360° to escape dead ends
   * behind the robot.
   */
  planRandomWalk(robot: Robot): void {
    const attempts = 12;
    for (let i = 0; i < attempts; i++) {
      const spread = i < 8 ? (Math.random() - 0.5) * Math.PI : Math.random() * Math.PI * 2;
      const angle = this.expeditionAngle + spread;
      const dist = 3 + Math.random() * 8;
      const tx = Math.floor(robot.x + Math.cos(angle) * dist);
      const ty = Math.floor(robot.y + Math.sin(angle) * dist);
      if (
        tx >= 0 && tx < this.labyrinth.width &&
        ty >= 0 && ty < this.labyrinth.height &&
        this.labyrinth.isWalkable(tx, ty)
      ) {
        this.planPathTo(robot, tx, ty);
        if (this.spline) return;
      }
    }
  }

  planPathTo(robot: Robot, goalX: number, goalY: number): void {
    const path = findPath(this.labyrinth, robot.tileX, robot.tileY, goalX, goalY);

    if (path && path.length > 1) {
      const smoothed = this.smoothPath(path);
      // Prepend current position for a seamless spline start
      const withStart: [number, number][] = [[robot.x, robot.y], ...smoothed.slice(1)];
      this.spline = new Spline(withStart);
      this.splineDist = 0;
    } else {
      this.clearPath();
    }
  }

  // =======================================================
  // Pure helpers — path smoothing + exploration scoring
  // =======================================================

  /**
   * Greedy shortcut smoothing: from each anchor, scan forward and keep
   * the furthest tile still reachable via a revealed line-of-walk.
   * Collapses zigzag grid paths into direct spline control points.
   */
  private smoothPath(tilePath: [number, number][]): [number, number][] {
    if (tilePath.length <= 2) {
      return tilePath.map(([tx, ty]) => [tx + 0.5, ty + 0.5] as [number, number]);
    }

    const lab = this.labyrinth;
    const exp = this.exploration;
    const first = tilePath[0]!;
    const result: [number, number][] = [[first[0] + 0.5, first[1] + 0.5]];
    let anchor = 0;

    while (anchor < tilePath.length - 1) {
      let furthest = anchor + 1;
      const a = tilePath[anchor]!;
      const ax = a[0] + 0.5;
      const ay = a[1] + 0.5;

      for (let probe = anchor + 2; probe < tilePath.length; probe++) {
        const p = tilePath[probe]!;
        const px = p[0] + 0.5;
        const py = p[1] + 0.5;
        if (hasRevealedLineOfWalk(lab, exp, ax, ay, px, py)) {
          furthest = probe;
        }
      }

      const f = tilePath[furthest]!;
      result.push([f[0] + 0.5, f[1] + 0.5]);
      anchor = furthest;
    }

    return result;
  }

  /**
   * Score each frontier tile (expedition direction + distance-from-base +
   * local unvisited potential + mild face-bias + noise), return the best.
   * Clears `unreachable` as a last resort when nothing scored.
   */
  private pickExplorationTarget(
    tileX: number, tileY: number, faceAngle: number,
  ): [number, number] | null {
    let bestTarget: [number, number] | null = null;
    let bestScore = -Infinity;

    const expDirX = Math.cos(this.expeditionAngle);
    const expDirY = Math.sin(this.expeditionAngle);

    const faceDirX = Math.cos(faceAngle);
    const faceDirY = Math.sin(faceAngle);

    const { frontier, unreachable, visited } = this.exploration;

    const labW = this.labyrinth.width;
    for (const key of frontier) {
      if (unreachable.has(key)) continue;
      if (visited.has(key)) continue;

      // Decode "x,y" key without allocation — parseInt stops at the comma.
      const comma = key.indexOf(',');
      const fx = parseInt(key, 10);
      const fy = parseInt(key.slice(comma + 1), 10);

      const dx = fx - tileX;
      const dy = fy - tileY;
      const dist = Math.abs(dx) + Math.abs(dy);
      if (dist > 120) continue;

      const distScore = -dist * 0.2;

      // Exploration potential — count unvisited walkable neighbours.
      let potential = 0;
      for (const [ddx, ddy] of ALL_DIRS) {
        if (
          this.labyrinth.isWalkable(fx + ddx, fy + ddy) &&
          !this.visitedNumeric.has((fy + ddy) * labW + (fx + ddx))
        ) {
          potential++;
        }
      }

      const baseDistX = fx - this.baseX;
      const baseDistY = fy - this.baseY;
      const baseDist = Math.hypot(baseDistX, baseDistY);
      const awayFromBase = baseDist * 0.15;

      // Expedition alignment: strong bias toward the random outward dir.
      const tDist = Math.hypot(baseDistX, baseDistY) || 1;
      const expDot = (baseDistX / tDist) * expDirX + (baseDistY / tDist) * expDirY;
      const expScore = expDot * 6;

      // Mild forward preference (no backward penalty).
      const faceDist = Math.hypot(dx, dy) || 1;
      const faceDot = (dx / faceDist) * faceDirX + (dy / faceDist) * faceDirY;
      const faceScore = Math.max(0, faceDot) * 2;

      const score = distScore + potential * 3 + awayFromBase + expScore + faceScore
        + Math.random() * 0.5;

      if (score > bestScore) {
        bestScore = score;
        bestTarget = [fx, fy];
      }
    }

    // No target found — reset the unreachable set so stale blockades clear.
    if (!bestTarget && unreachable.size > 0) {
      unreachable.clear();
    }

    return bestTarget;
  }
}
